import { Controller } from '@hotwired/stimulus'
import consumer from '../channels/consumer'

export default class extends Controller {
  static targets = ['list']
  static values = {
    userId: Number,
    unreadCount: { type: Number, default: 0 }
  }

  connect () {
    this.seenMessageIds = new Set()
    this.renderUnreadBadges(this.unreadCountValue)
    this.unlockAudio = () => this.prepareAudio()
    document.addEventListener('pointerdown', this.unlockAudio, { once: true })
    document.addEventListener('keydown', this.unlockAudio, { once: true })

    this.subscription = consumer.subscriptions.create(
      { channel: 'UserChatNotificationsChannel' },
      { received: (data) => this.receive(data) }
    )
  }

  disconnect () {
    if (this.subscription) this.subscription.unsubscribe()
    document.removeEventListener('pointerdown', this.unlockAudio)
    document.removeEventListener('keydown', this.unlockAudio)
    if (this.audioContext) this.audioContext.close().catch(() => {})
  }

  receive (data) {
    if (data?.type !== 'chat_message') return
    if (Number(data.recipient_id) !== this.userIdValue) return

    const messageId = String(data.message_id || '')
    if (!messageId || this.seenMessageIds.has(messageId)) return
    this.seenMessageIds.add(messageId)

    if (this.activeConversationId() === String(data.conversation_id)) return

    this.unreadCountValue = Number(data.total_unread_count) || 1
    this.renderUnreadBadges(this.unreadCountValue)
    this.showToast(data)
    this.playBeep()
  }

  activeConversationId () {
    return document.querySelector('[data-chat-conversation-id]')?.dataset.chatConversationId || ''
  }

  renderUnreadBadges (count) {
    const normalizedCount = Math.max(Number(count) || 0, 0)

    document.querySelectorAll('[data-chat-unread-badge]').forEach((badge) => {
      badge.textContent = normalizedCount > 99 ? '99+' : String(normalizedCount)
      badge.classList.toggle('hidden', normalizedCount === 0)
      badge.setAttribute(
        'aria-label',
        `${normalizedCount} unread ${normalizedCount === 1 ? 'message' : 'messages'}`
      )
    })
  }

  showToast (data) {
    if (!this.hasListTarget) return

    const toast = document.createElement('div')
    toast.className = [
      'pointer-events-auto relative block w-full translate-y-[-0.5rem] rounded-2xl border border-slate-200',
      'bg-white/95 p-3.5 opacity-0 shadow-2xl ring-1 ring-black/5 backdrop-blur',
      'transition duration-300 hover:border-brand-200 hover:bg-white'
    ].join(' ')

    const link = document.createElement('a')
    link.href = data.path || `/conversations/${data.conversation_id}`
    link.className = 'block pr-7'

    const row = document.createElement('div')
    row.className = 'flex items-start gap-3'

    const icon = document.createElement('span')
    icon.className = 'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-brand text-white shadow-sm'
    icon.innerHTML = '<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>'

    const copy = document.createElement('span')
    copy.className = 'min-w-0 flex-1'

    const eyebrow = document.createElement('span')
    eyebrow.className = 'block text-[10px] font-extrabold uppercase tracking-[0.16em] text-brand-600'
    eyebrow.textContent = 'New message'

    const sender = document.createElement('span')
    sender.className = 'mt-0.5 block truncate text-sm font-extrabold text-slate-900'
    sender.textContent = data.sender_name || 'New message'

    const subject = document.createElement('span')
    subject.className = 'block truncate text-[11px] font-semibold text-slate-500'
    subject.textContent = data.subject || 'Chat'

    const preview = document.createElement('span')
    preview.className = 'mt-1 block line-clamp-2 text-xs leading-relaxed text-slate-600'
    preview.textContent = data.preview || 'Open chat to view the message.'

    const badge = document.createElement('span')
    badge.className = 'inline-flex min-w-[1.35rem] shrink-0 items-center justify-center rounded-full bg-rose-500 px-1.5 py-1 text-[10px] font-extrabold leading-none text-white'
    badge.textContent = Number(data.unread_count) > 99 ? '99+' : String(Number(data.unread_count) || 1)

    const closeButton = document.createElement('button')
    closeButton.type = 'button'
    closeButton.className = 'absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400'
    closeButton.setAttribute('aria-label', 'Dismiss message notification')
    closeButton.innerHTML = '<svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.25" d="M6 18L18 6M6 6l12 12"/></svg>'
    closeButton.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.dismissToast(toast)
    })

    copy.append(eyebrow, sender, subject, preview)
    row.append(icon, copy, badge)
    link.append(row)
    toast.append(link, closeButton)
    this.listTarget.prepend(toast)

    requestAnimationFrame(() => {
      toast.classList.remove('translate-y-[-0.5rem]', 'opacity-0')
    })

    window.setTimeout(() => {
      this.dismissToast(toast)
    }, 7000)
  }

  dismissToast (toast) {
    if (!toast?.isConnected || toast.dataset.dismissing === 'true') return

    toast.dataset.dismissing = 'true'
    toast.classList.add('translate-y-[-0.5rem]', 'opacity-0')
    window.setTimeout(() => toast.remove(), 300)
  }

  prepareAudio () {
    if (this.audioContext) {
      if (this.audioContext.state === 'suspended') this.audioContext.resume().catch(() => {})
      return
    }

    const AudioContext = window.AudioContext || window.webkitAudioContext
    if (!AudioContext) return
    this.audioContext = new AudioContext()
    this.audioContext.resume().catch(() => {})
  }

  playBeep () {
    this.prepareAudio()
    if (!this.audioContext || this.audioContext.state !== 'running') return

    const now = this.audioContext.currentTime
    const notes = [
      { frequency: 659.25, delay: 0, duration: 0.2, volume: 0.055 },
      { frequency: 987.77, delay: 0.1, duration: 0.3, volume: 0.045 }
    ]

    notes.forEach(({ frequency, delay, duration, volume }) => {
      const oscillator = this.audioContext.createOscillator()
      const gain = this.audioContext.createGain()
      const start = now + delay
      const end = start + duration

      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(frequency, start)
      gain.gain.setValueAtTime(0.0001, start)
      gain.gain.exponentialRampToValueAtTime(volume, start + 0.015)
      gain.gain.exponentialRampToValueAtTime(0.0001, end)
      oscillator.connect(gain)
      gain.connect(this.audioContext.destination)
      oscillator.start(start)
      oscillator.stop(end)
    })
  }
}
